import { IFlightMarkersState } from "../../modules/flightMarkers/flightMarkersReducer";
import { IGCPRequestState } from "../../modules/gcpRequest/reducer";
import { getSiteSrs, ISiteState } from "../../modules/site/reducer";
import { getPlacedMarkersRows, IGcpRowItem } from "../exportGcpRows";
import { getAllGcps, getToBeCheckedGcps } from "../gpcExporters";

const sanitizeTextProperty = (value: string | null) => {
  if (!value) {
    return "";
  }

  if (!value.includes(",") && !value.includes('"')) {
    return value;
  }

  return '"' + value.replace(/"/g, '""') + '"';
};

const getNECsvHeader = () => {
  return "Name,Northing,Easting,Elevation,Description\n";
};

const getLLCsvHeader = () => {
  return "Name,Latitude,Longitude,Elevation,Description\n";
};

const getCsvContent = (rows: IGcpRowItem[]) => {
  return rows
    .map(
      ({ name, x, y, z, description }) =>
        `${sanitizeTextProperty(name)},${y},${x},${z},${sanitizeTextProperty(
          description,
        )}`,
    )
    .join("\n");
};

const ensureSrs = (siteState: ISiteState) => {
  const srs = getSiteSrs(siteState);
  if (!srs) {
    throw new Error("SRS is required to create flight markers CSV");
  }
};

export const createGcpRequestCsvProblem = (
  gcpRequestState: IGCPRequestState,
  flightMarkersState: IFlightMarkersState,
  siteState: ISiteState,
  forceGlobal = false,
) => {
  ensureSrs(siteState);

  const gcps = getToBeCheckedGcps(
    gcpRequestState,
    flightMarkersState,
    siteState,
    forceGlobal ? "global" : "site_srs",
  );
  const header = getNECsvHeader();
  const content = getCsvContent(gcps);
  return header.concat(content);
};

export const createGcpRequestCsvProblemInWgs = (
  gcpRequestState: IGCPRequestState,
  flightMarkersState: IFlightMarkersState,
  siteState: ISiteState,
) => {
  ensureSrs(siteState);

  const gcps = getToBeCheckedGcps(
    gcpRequestState,
    flightMarkersState,
    siteState,
    "wgs84",
  );
  const header = getLLCsvHeader();
  const content = getCsvContent(gcps);
  return header.concat(content);
};

export const createGcpRequestCsvAll = (
  gcpRequestState: IGCPRequestState,
  flightMarkersState: IFlightMarkersState,
  siteState: ISiteState,
  forceGlobal = false,
) => {
  ensureSrs(siteState);

  const gcps = getAllGcps(
    gcpRequestState,
    flightMarkersState,
    siteState,
    forceGlobal ? "global" : "site_srs",
  );
  const header = getNECsvHeader();
  const content = getCsvContent(gcps);
  return header.concat(content);
};

export const createGcpRequestCsvAllInWgs = (
  gcpRequestState: IGCPRequestState,
  flightMarkersState: IFlightMarkersState,
  siteState: ISiteState,
) => {
  ensureSrs(siteState);

  const gcps = getAllGcps(
    gcpRequestState,
    flightMarkersState,
    siteState,
    "wgs84",
  );
  const header = getLLCsvHeader();
  const content = getCsvContent(gcps);
  return header.concat(content);
};

// Export on pilot page
export const createPlacedOnlyCsv = (
  flightMarkersState: IFlightMarkersState,
  siteState: ISiteState,
  forceGlobal = false,
) => {
  ensureSrs(siteState);

  const flightMarkersRows = getPlacedMarkersRows(
    flightMarkersState,
    siteState,
    forceGlobal ? "global" : "site_srs",
  );
  const header = getNECsvHeader();
  const content = getCsvContent(flightMarkersRows);
  return header.concat(content);
};

// Export on pilot page
export const createPlacedOnlyCsvInWgs = (
  flightMarkersState: IFlightMarkersState,
  siteState: ISiteState,
) => {
  ensureSrs(siteState);

  const flightMarkersRows = getPlacedMarkersRows(
    flightMarkersState,
    siteState,
    "wgs84",
  );
  const header = getLLCsvHeader();
  const content = getCsvContent(flightMarkersRows);
  return header.concat(content);
};
